/**
 * Interests ReAct Agent for IAB Taxonomy Classification.
 *
 * Uses ReAct pattern (Reason-Act-Observe) with taxonomy search tools
 * to extract and validate interest-related classifications from emails.
 *
 * Integrates with existing AnalyzerLLMClient infrastructure.
 *
 * 1:1 Port of: src/email_parser/agents/interests_agent.py (307 lines)
 */
import { INTERESTS_AGENT_SYSTEM_PROMPT, INTERESTS_AGENT_USER_PROMPT, INTERESTS_EVIDENCE_GUIDELINES, } from '../llm/prompts';
import { getCachedTaxonomyContext } from '../llm/taxonomyContext';
import { evaluate_evidence_quality_batch, adjust_confidence_with_evidence_quality, should_block_classification, } from '../llm/evidenceJudge';
import { validateClassification } from '../analyzers/tools';
/**
 * Extract interests classifications using ReAct-style agent with tools.
 *
 * This implements a simplified ReAct pattern compatible with AnalyzerLLMClient.
 * The agent can use tools (search, validate) and reflect on validation failures.
 *
 * Python lines 23-274
 *
 * @param emails - List of email dicts with 'subject', 'sender', 'body'
 * @param llm_client - AnalyzerLLMClient instance
 * @param max_iterations - Max reflection loops (default: 1)
 * @returns Classification result with iterations and tool_calls tracked
 *
 * @example
 * import { AnalyzerLLMClient } from '../llm/client'
 * const llm_client = new AnalyzerLLMClient({ provider: "openai" })
 * const emails = [{subject: "...", body: "..."}]
 * const result = await extract_interests_with_agent({ emails, llm_client })
 */
export async function extract_interests_with_agent(params) {
    const { emails, llm_client, evidence_judge_client = llm_client, max_iterations = 1 } = params; // Python line 26
    // Get taxonomy context for prompt                                            // Python line 56
    const taxonomy_context = getCachedTaxonomyContext('interests'); // Python line 57
    // Format emails for prompt and create email_number → email_id mapping        // Python line 59
    const email_number_to_id = {}; // Map email numbers (1,2,3...) to actual email IDs // Python line 60
    const email_text_parts = []; // Python line 61
    const emails_batch = emails.slice(0, 50); // Process up to 50 emails per batch // Python line 63
    for (let i = 0; i < emails_batch.length; i++) { // Python line 63
        const email = emails_batch[i];
        const email_number = i + 1; // Python line 64
        const email_id = email.id || `unknown_${i}`; // Python line 65
        email_number_to_id[email_number] = email_id; // Python line 66
        // Handle NaN values from pandas CSV (float type, not string)              // Python line 68
        let summary_content = email.summary || ''; // Python line 69
        let subject = email.subject || 'N/A'; // Python line 70
        let sender = email.from || 'N/A'; // Python line 71
        // Convert pandas NaN (float) to empty string                              // Python line 73
        if (typeof summary_content !== 'string') { // Python line 74
            summary_content = summary_content === null || summary_content === undefined || Number.isNaN(Number(summary_content)) ? '' : String(summary_content); // Python line 75
        }
        if (typeof subject !== 'string') { // Python line 76
            subject = subject === null || subject === undefined || Number.isNaN(Number(subject)) ? 'N/A' : String(subject); // Python line 77
        }
        if (typeof sender !== 'string') { // Python line 78
            sender = sender === null || sender === undefined || Number.isNaN(Number(sender)) ? 'N/A' : String(sender); // Python line 79
        }
        console.info(`Email ${email_number}: summary_len=${summary_content.length}`); // Python line 81
        if (!summary_content) { // Python line 82
            console.warn(`Email ${email_number} has empty summary field!`); // Python line 83
        }
        email_text_parts.push(// Python line 85
        `Email ${email_number}:\n` + // Python line 86
            `Subject: ${subject}\n` + // Python line 87
            `From: ${sender}\n` + // Python line 88
            `Summary: ${summary_content.slice(0, 500)}...` // Python line 89
        );
    }
    const email_text = email_text_parts.join('\n\n'); // Python line 92
    // Build initial prompt with taxonomy context                                // Python line 98
    const system_prompt_with_taxonomy = `${INTERESTS_AGENT_SYSTEM_PROMPT}

## Available Taxonomy Entries

${taxonomy_context}

IMPORTANT:
- Select the taxonomy_id from the list above (the number after "ID")
- For the "value" field, use ONLY the FINAL tier value (rightmost part after last "|")
- Example: For "ID 688: Interest | Technology & Computing | Artificial Intelligence", use taxonomy_id=688 and value="Artificial Intelligence"
- AVOID category entries (short paths with 2-3 tiers) - choose the most specific entry (4+ tiers when available)
- Do NOT include the full tier path in the value field`; // Python line 109
    let user_prompt = INTERESTS_AGENT_USER_PROMPT.replace('{email_batch}', email_text).replace('{batch_size}', String(emails_batch.length)); // Python line 111
    // Track agent execution                                                     // Python line 116
    let tool_calls = 0; // Python line 117
    let iterations = 0; // Python line 118
    let classifications = []; // Python line 119
    try { // Python line 121
        for (let iteration = 0; iteration < max_iterations; iteration++) { // Python line 122
            iterations++; // Python line 123
            console.info(`Interests agent iteration ${iteration + 1}/${max_iterations}`); // Python line 124
            // Call LLM (analyze_email returns parsed JSON with "classifications" key) // Python line 126
            // max_tokens auto-calculated from model's context window                // Python line 127
            const response = await llm_client.analyze_email({
                prompt: `${system_prompt_with_taxonomy}\n\n${user_prompt}`, // Python line 129
                temperature: 0.1, // Python line 130
            });
            // Extract classifications directly from parsed response                 // Python line 133
            const parsed_classifications = response.classifications || []; // Python line 134
            // ===== ENHANCED LOGGING: Raw LLM Response =====                        // Python line 136
            console.info(`📝 Interests LLM Response: ${parsed_classifications.length} classifications returned`); // Python line 137
            if (parsed_classifications.length === 0) { // Python line 144
                console.warn('No classifications found in agent output'); // Python line 145
                break; // Python line 146
            }
            // Validate each classification (taxonomy validation only)               // Python line 148
            let all_valid = true; // Python line 149
            const taxonomy_valid_classifications = []; // Python line 150
            for (const classification of parsed_classifications) { // Python line 152
                const taxonomy_id = classification.taxonomy_id; // Python line 153
                const value = classification.value; // Python line 154
                if (!taxonomy_id || !value) { // Python line 156
                    continue; // Python line 157
                }
                // Validate using tool                                                 // Python line 159
                tool_calls++; // Python line 160
                const validation_result_json = validateClassification(taxonomy_id, value); // Python line 161
                const validation_result = JSON.parse(validation_result_json.result || '{}'); // Python line 161
                if (validation_result.valid) { // Python line 168
                    // Taxonomy validation passed - add to batch for evidence judge      // Python line 169
                    taxonomy_valid_classifications.push(classification); // Python line 170
                }
                else { // Python line 171
                    // Validation failed - agent needs to reflect                        // Python line 172
                    all_valid = false; // Python line 173
                    const expected_value = validation_result.expected_value || ''; // Python line 174
                    console.info('Validation failed for one interests classification'); // Python line 175
                    // Update prompt for reflection                                      // Python line 180
                    user_prompt += // Python line 181
                        `\n\nREFLECTION: Classification ${taxonomy_id}=${value} is INVALID. ` +
                            `Expected value: '${expected_value}'. ` +
                            `Please search again and use the correct taxonomy_id for '${expected_value}'.`;
                    break; // Trigger reflection loop                                    // Python line 186
                }
            }
            // ===== BATCH EVIDENCE QUALITY VALIDATION =====                         // Python line 188
            // Use parallel LLM-as-Judge for all taxonomy-valid classifications      // Python line 189
            let validated_classifications = [];
            if (taxonomy_valid_classifications.length > 0) { // Python line 190
                // Evaluate all classifications in parallel                            // Python line 198
                // Pass actual batch size to distinguish true hallucinations from context truncation // Python line 199
                const actual_batch_size = emails_batch.length; // Python line 200
                const evidence_evals = await evaluate_evidence_quality_batch({
                    classifications: taxonomy_valid_classifications, // Python line 202
                    email_context: email_text, // Python line 203
                    section_guidelines: INTERESTS_EVIDENCE_GUIDELINES, // Python line 204
                    llm_client: evidence_judge_client, // Python line 205
                    max_workers: 5, // Python line 206
                    actual_batch_size, // Python line 207
                });
                // Process results                                                     // Python line 210
                for (let i = 0; i < taxonomy_valid_classifications.length; i++) { // Python line 212
                    let classification = taxonomy_valid_classifications[i];
                    const evidence_eval = evidence_evals[i];
                    // Adjust confidence based on evidence quality                       // Python line 213
                    classification = adjust_confidence_with_evidence_quality(// Python line 214
                    classification, evidence_eval);
                    // ===== ENHANCED LOGGING: Evidence Judge Decision =====             // Python line 218
                    const will_block = should_block_classification(evidence_eval.quality_score); // Python line 219
                    console.info(// Python line 220
                    `🔍 Evidence Judge: ` +
                        `quality=${evidence_eval.quality_score.toFixed(2)}, ` +
                        `type=${evidence_eval.evidence_type || 'N/A'}, ` +
                        `decision=${will_block ? 'BLOCK' : 'PASS'}`);
                    // Block completely inappropriate inferences                         // Python line 227
                    if (will_block) { // Python line 228
                        console.warn(// Python line 229
                        `Blocked inappropriate interest inference ` +
                            `(quality_score=${evidence_eval.quality_score.toFixed(2)}, ` +
                            `issue=${evidence_eval.issue || 'N/A'})`);
                        continue; // Skip this classification                              // Python line 234
                    }
                    // Map email_numbers array to email_ids for provenance tracking      // Python line 236
                    let email_numbers = classification.email_numbers || []; // Python line 237
                    // Backward compatibility: check for old single email_number field   // Python line 239
                    if (email_numbers.length === 0) { // Python line 240
                        const email_number = classification.email_number; // Python line 241
                        if (email_number) { // Python line 242
                            email_numbers = [email_number]; // Python line 243
                        }
                    }
                    if (email_numbers.length > 0 && email_numbers.every((n) => n in email_number_to_id)) { // Python line 245
                        classification.email_ids = email_numbers.map((n) => email_number_to_id[n]); // Python line 246
                        // FIX: Explode classifications with multiple email_ids into separate classifications (one per email)
                        // This ensures each email gets its own classification with a single email_id field
                        for (const email_id of classification.email_ids) {
                            validated_classifications.push({
                                ...classification,
                                email_id, // Singular email_id for this specific email
                                email_ids: [email_id], // Keep array for backward compatibility
                            });
                        }
                    }
                    else { // Python line 247
                        console.warn('Classification missing email_numbers or invalid'); // Python line 248
                        // For classifications without valid email tracking, skip them (don't add to validated list)
                        // This prevents the "assign to all emails" bug in the API route
                    }
                }
            }
            if (all_valid && validated_classifications.length > 0) { // Python line 255
                // All classifications validated - we're done                          // Python line 256
                classifications = validated_classifications; // Python line 257
                console.info(`Agent converged with ${classifications.length} validated classifications`); // Python line 258
                break; // Python line 259
            }
        }
        return {
            classifications, // Python line 262
            iterations, // Python line 263
            tool_calls, // Python line 264
        };
    }
    catch (error) { // Python line 267
        console.error(`Interests agent error: ${error}`, error); // Python line 268
        return {
            classifications: [], // Python line 270
            iterations, // Python line 271
            tool_calls, // Python line 272
            error: String(error), // Python line 273
        };
    }
}
//# sourceMappingURL=interests.js.map